import { useMemo } from 'react';
import { useHolidayBannerQuery } from 'store/graphql-strapi';
import { useLocale } from 'store/locale';

export type HolidayBannerType = 'text' | 'link';

export interface HolidayBannerView {
  type: HolidayBannerType;
  title: string;
  imageUrl: string | null;
}

interface UseHolidayBannerResult {
  banner: HolidayBannerView | null;
  loading: boolean;
}

export function useHolidayBanner(): UseHolidayBannerResult {
  const { locale } = useLocale();

  const { data, loading } = useHolidayBannerQuery({
    variables: { locale: locale.name },
    skip: !locale.name
  });

  const banner = useMemo<HolidayBannerView | null>(() => {
    const attrs = data?.holidayBanner?.data?.attributes;
    if (!attrs) {
      return null;
    }

    const now = Date.now();
    const start = attrs.startsAt ? new Date(attrs.startsAt).getTime() : NaN;
    const end = attrs.endsAt ? new Date(attrs.endsAt).getTime() : NaN;
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return null;
    }
    if (now < start || now > end) {
      return null;
    }

    const type = attrs.type === 'link' ? 'link' : 'text';
    const imageUrl = attrs.image?.data?.attributes?.url ?? null;

    if (type === 'link' && !imageUrl) {
      // Конфликтная запись (link без картинки) — на бэке поймано в lifecycle,
      // здесь страхуемся, чтобы не отрендерить кликабельную ссылку в никуда.
      return null;
    }

    return {
      type,
      title: attrs.title ?? '',
      imageUrl
    };
  }, [data]);

  return { banner, loading };
}
